/** @jsxImportSource @emotion/react */
import Image from 'next/image';
import { useEffect, useRef, useState } from 'react';

import ChevronBigDownSvg from './chevron_big_down.svg';

import type { Theme } from '@emotion/react';
import type { Interpolation } from '@emotion/serialize';

type AccordionProps = {
  content: string | React.ReactNode;
  title: string;
  unfoldIcon?: { src: string; alt: string };
  collapseIcon?: { src: string; alt: string };
  cssProps?: Interpolation<Theme>;
  open?: boolean;
  trackClick?: (title: string) => void;
};

const contentClass = {
  marginBottom: '1.5rem',
  padding: '1rem 1.5rem',
  opacity: '0',
  width: 'auto'
} as const;

const summaryClass = (t: Theme) =>
  ({
    padding: '1rem 2rem 1rem 1rem',
    display: 'flex',
    borderTop: `1px solid ${t.color.grey.g92}`,
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '.5rem',
    ':hover': {
      backgroundColor: 'hsl(0, 0%, 98%)'
    },
    '::marker, ::-webkit-details-marker': {
      content: 'none',
      display: 'none'
    }
  }) as const;

const chevronClass = {
  display: 'block',
  height: '18px',
  left: '0.75rem',
  opacity: '0.8',
  transform: 'rotate(-90deg)',
  transition: 'transform 250ms ease-out',
  transformOrigin: 'center',
  width: '18px'
};

const Accordion: React.FC<AccordionProps> = ({
  content,
  cssProps,
  unfoldIcon,
  collapseIcon,
  trackClick: trackClick,
  open: openProps = true,
  title
}) => {
  const [open, setOpen] = useState(openProps);
  const accordion = useRef<HTMLDetailsElement>(null);
  const summary = useRef<HTMLElement>(null);
  const contentEl = useRef<HTMLDivElement>(null);
  const onClick = (e) => {
    e.preventDefault();
    if (typeof trackClick === 'function') {
      trackClick(title);
    }
    setOpen(!open);
  };

  useEffect(() => {
    const summaryHeight = summary.current?.offsetHeight || 0;
    const contentHeight = contentEl.current?.offsetHeight || 0;
    const accordionHeight = accordion.current?.offsetHeight || 0;
    const animationOptions = { duration: 180, easing: 'ease-in' };
    if (open) {
      accordion.current?.animate(
        {
          height: [`${summaryHeight}px`, `${summaryHeight + contentHeight}px`]
        },
        animationOptions
      );
      if (contentEl.current?.style) {
        contentEl.current.style.opacity = '1';
      }
    } else {
      accordion.current?.animate(
        { height: [`${accordionHeight}px`, `${summaryHeight}px`] },
        animationOptions
      );
      if (contentEl.current?.style) {
        contentEl.current.style.opacity = '0';
      }
    }
  }, [open]);

  return (
    <details
      ref={accordion}
      // className={`${style.container} ${className}`}
      css={{
        cursor: 'default',
        overflow: 'hidden',
        width: '100%',
        ...(cssProps as object)
      }}
      open={open}
    >
      <summary ref={summary} css={summaryClass} onClick={onClick}>
        <div>{title}</div>
        {unfoldIcon && collapseIcon ? (
          <>
            {open ? (
              <Image
                src={collapseIcon?.src}
                width={24}
                height={24}
                alt={collapseIcon?.alt}
                unoptimized
              ></Image>
            ) : (
              <Image src={unfoldIcon.src} width={24} height={24} alt={unfoldIcon.alt} unoptimized></Image>
            )}
          </>
        ) : (
          <ChevronBigDownSvg css={chevronClass} style={open ? { transform: 'rotate(0)' } : {}} />
        )}
      </summary>
      {typeof content === 'string' ? (
        <div ref={contentEl} css={contentClass} dangerouslySetInnerHTML={{ __html: content }} />
      ) : (
        <div ref={contentEl} css={contentClass}>
          {content}
        </div>
      )}
    </details>
  );
};

export default Accordion;
